/**
 * Starter kits (moat plan §6.2) — Indonesia-first idea templates shown under the idea box.
 * Clicking a kit fills the textarea; when the kit names part of a stack we carry it to the
 * tech step by PATCHing the fresh plan's `techStack` right after it is created.
 */
import { useState } from 'react'
import { Sparkles } from 'lucide-react'
import { kitsFor, type StarterKit } from '#/lib/templates'
import { AI_PICK, BACKEND_OPTIONS, DATABASE_OPTIONS, DEPLOYMENT_OPTIONS, FRONTEND_OPTIONS } from '#/lib/tech-options'
import type { TechStack } from '#/lib/types'
import { cn } from '#/lib/utils'
import { m } from '#/paraglide/messages'

const VISIBLE = 4

/** Kit `tech` values are short slugs ('vps', 'supabase'); map them onto the exact wizard options. */
const resolve = (options: string[], value: string | undefined) => {
  if (!value) return AI_PICK
  const v = value.toLowerCase()
  return options.find((o) => o.toLowerCase() === v) ?? options.find((o) => o.toLowerCase().includes(v)) ?? AI_PICK
}

/** `null` when the kit says nothing about the stack — nothing to send in that case. */
export function kitTechStack(kit: StarterKit): TechStack | null {
  if (!kit.tech) return null
  const stack: TechStack = {
    frontend: resolve(FRONTEND_OPTIONS, kit.tech.frontend),
    backend: resolve(BACKEND_OPTIONS, kit.tech.backend),
    database: resolve(DATABASE_OPTIONS, kit.tech.database),
    deployment: resolve(DEPLOYMENT_OPTIONS, kit.tech.deployment),
  }
  return Object.values(stack).every((v) => v === AI_PICK) ? null : stack
}

export function StarterKits({ locale, selectedId, onPick, className }: { locale: 'en' | 'id'; selectedId: string | null; onPick: (kit: StarterKit) => void; className?: string }) {
  const [all, setAll] = useState(false)
  const kits = kitsFor(locale)
  const shown = all ? kits : kits.slice(0, VISIBLE)
  return (
    <section className={cn('space-y-2.5', className)} aria-label={m.fw_kits_title()}>
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold">
          <Sparkles className="size-4 text-primary" aria-hidden /> {m.fw_kits_title()}
        </h2>
        {kits.length > VISIBLE ? (
          <button type="button" className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline" onClick={() => setAll((v) => !v)}>
            {all ? m.fw_kits_less() : m.fw_kits_more({ n: kits.length })}
          </button>
        ) : null}
      </div>
      <p className="text-xs text-muted-foreground">{m.fw_kits_hint()}</p>
      <ul className="grid gap-2 sm:grid-cols-2">
        {shown.map((k) => (
          <li key={k.id}>
            <button
              type="button"
              onClick={() => onPick(k)}
              aria-pressed={selectedId === k.id}
              className={cn(
                'flex h-full w-full flex-col gap-1 rounded-xl border bg-card px-3 py-2.5 text-left transition-colors hover:border-primary/60 hover:bg-muted/50',
                selectedId === k.id && 'border-primary bg-primary/5',
              )}
            >
              <span className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{k.title[locale]}</span>
                <span className="shrink-0 rounded-full border px-1.5 py-px text-[10px] uppercase tracking-wide text-muted-foreground">
                  {k.region === 'id' ? m.fw_kits_region_id() : m.fw_kits_region_global()}
                </span>
              </span>
              <span className="text-xs leading-snug text-muted-foreground">{k.tagline[locale]}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}
